import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import TestCaseForm from '../components/TestCaseForm';
import Pagination from '../components/Pagination';
import { useIssueTracker } from '../hooks/useIssueTracker';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { ResizableTable, ResizableHeader } from '../components/ResizableTable';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function TestCases() {
  const [searchParams] = useSearchParams();
  const { projectKey: routeProjectKey } = useParams();
  // Support both URL params (/projects/:projectKey/testcases) and query params (?projectKey=...)
  const projectKey = routeProjectKey || searchParams.get('projectKey');
  const folderId = searchParams.get('folderId');
  const suiteId = searchParams.get('suiteId');
  const [projectId, setProjectId] = useState(null);
  const { isEnabled: trackerEnabled, isJira, labels: trackerLabels } = useIssueTracker(projectId);

  const TC_COLS = [
    { key: 'check',   defaultWidth: 36 },
    { key: 'id',      defaultWidth: 110 },
    { key: 'summary', defaultWidth: 340 },
    { key: 'suite',   defaultWidth: 160 },
    { key: 'steps',   defaultWidth: 90 },
    { key: 'tracker', defaultWidth: 160 },
    { key: 'actions', defaultWidth: 70 },
  ];
  const { colWidths, onMouseDown, tableRef } = useResizableColumns('zipqa-testcases-cols', TC_COLS);

  const [cases, setCases] = useState([]);
  const [suites, setSuites] = useState([]);
  const [project, setProject] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentSuite, setCurrentSuite] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps: '',
    expectedResult: '',
    testSuite: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [editingCase, setEditingCase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSuite, setFilterSuite] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedCases, setSelectedCases] = useState([]);
  const [importResult, setImportResult] = useState(null);

  // JIRA state
  const [jiraModal, setJiraModal] = useState(null); // { mode: 'link'|'create', testCase }
  const [jiraIssueKey, setJiraIssueKey] = useState('');
  const [jiraProjectKey, setJiraProjectKey] = useState('');
  const [jiraIssueType, setJiraIssueType] = useState('Bug');
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraError, setJiraError] = useState('');
  // ── JIRA Search state ─────────────────────────────────────────────────────
  const [jiraProjects, setJiraProjects] = useState([]);
  const [jiraConnected, setJiraConnected] = useState(false);
  // ── Active action dropdown ────────────────────────────────────────────────
  const [openActionMenu, setOpenActionMenu] = useState(null); // testCaseId
  // ──────────────────────────────────────────────────────────────────────────
  const [jiraLinkMode, setJiraLinkMode] = useState('key'); // 'key' | 'search' | 'create'
  const [searchProjectKey, setSearchProjectKey] = useState('');
  const [searchIssueType, setSearchIssueType] = useState('');
  const [issueTypes, setIssueTypes] = useState([]);
  const [jiraSearchResults, setJiraSearchResults] = useState([]);
  const [jiraSearching, setJiraSearching] = useState(false);
  const [jiraSearchError, setJiraSearchError] = useState('');
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (projectKey) {
      fetchProject();
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectId) {
      setCurrentPage(1);
      fetchCases();
      fetchSuites();
    }
  }, [projectId, folderId, suiteId]);

  useEffect(() => {
    if (projectId && (currentPage > 1 || pageSize !== 50)) {
      fetchCases();
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (folderId && folderId !== 'none' && folderId !== 'orphaned') {
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => setCurrentFolder(data?.data || data || null))
        .catch(() => setCurrentFolder(null));
    } else {
      setCurrentFolder(null);
    }
  }, [folderId]);

  useEffect(() => {
    if (suiteId) {
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/suites/${suiteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => setCurrentSuite(data?.data || data || null))
        .catch(() => setCurrentSuite(null));
    } else {
      setCurrentSuite(null);
    }
  }, [suiteId]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
          setProjectId(projectData.id || projectData._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchCases = async () => {
    try {
      setLoading(true);
      // Filter by projectId, folderId and suiteId if available
      const params = {};
      if (projectId) params.projectId = projectId;
      if (folderId) params.folderId = folderId;
      if (suiteId) params.suiteId = suiteId;
      params.page = currentPage;
      params.limit = pageSize;
      const response = await api.get('/testcases', { params });
      setCases(response.data.data || response.data.testCases || []);
      if (response.data.pagination) {
        setTotalItems(response.data.pagination.total || 0);
        setTotalPages(response.data.pagination.pages || 1);
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch test cases: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchSuites = async () => {
    try {
      // Filter by projectId if available
      const params = projectId ? { projectId } : {};
      const response = await api.get('/suites', { params });
      setSuites(response.data.data || response.data.testSuites || []);
    } catch (err) {
      console.error('Failed to fetch test suites:', err);
    }
  };

  const openCreateModal = () => {
    setFormData({
      title: '',
      description: '',
      steps: '',
      expectedResult: '',
      testSuite: '',
    });
    setEditingCase(null);
    setShowModal(true);
  };

  const openEditModal = testCase => {
    setFormData({
      title: testCase.title,
      description: testCase.description || '',
      steps: Array.isArray(testCase.steps) ? testCase.steps.join('\n') : testCase.steps || '',
      expectedResult: testCase.expectedResult || '',
      testSuite:
        typeof testCase.testSuite === 'object'
          ? testCase.testSuite._id || testCase.testSuite.id
          : testCase.testSuite || '',
    });
    setEditingCase(testCase);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCase(null);
    setFormData({
      title: '',
      description: '',
      steps: '',
      expectedResult: '',
      testSuite: '',
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        steps: formData.steps.split('\n').filter(s => s.trim()),
      };

      if (editingCase) {
        await api.put(`/testcases/${editingCase._id || editingCase.id}`, payload);
      } else {
        await api.post('/testcases', payload);
      }

      closeModal();
      fetchCases();
      setError('');
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save test case: ' + (errorData?.error || errorData?.message || err.message);
      if (errorData?.details) {
        errorMessage += ' - ' + errorData.details;
      }
      setError(errorMessage);
    }
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this test case?')) {
      try {
        await api.delete(`/testcases/${id}`);
        fetchCases();
        setError('');
      } catch (err) {
        setError('Failed to delete test case: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCases.length === 0) {
      setError('No test cases selected for deletion');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedCases.length} test case(s)? This cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;
      
      for (const caseId of selectedCases) {
        try {
          await api.delete(`/testcases/${caseId}`);
          successCount++;
        } catch (err) {
          failCount++;
        }
      }
      
      setSelectedCases([]);
      fetchCases();
      
      if (failCount === 0) {
        setError(`Successfully deleted ${successCount} test case(s)`);
        setTimeout(() => setError(''), 3000);
      } else {
        setError(`Deleted ${successCount} test case(s), ${failCount} failed`);
      }
    } catch (err) {
      setError('Failed to delete test cases: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const response = await api.get('/testcases/export/csv', {
        responseType: 'blob',
      });
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `test-cases-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      
      setShowExportModal(false);
      setError('');
    } catch (err) {
      setError('Failed to export test cases: ' + (err.response?.data?.message || err.message));
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // CSV Template with new column order (no ID, Owner, JIRA Status, Project Key)
    // Linked Requirement Keys: use REQ-NNN IDs (e.g. REQ-001;REQ-002) separated by semicolons or commas
    const idCol = trackerEnabled ? trackerLabels.idLabel : null;
    const urlCol = trackerEnabled ? trackerLabels.urlLabel : null;
    const trackerCols = trackerEnabled ? `,${idCol},${urlCol}` : '';
    const trackerVals1 = trackerEnabled ? ',"",""' : '';
    const trackerVals2 = trackerEnabled
      ? (isJira ? ',"PROJ-456","https://jira.example.com/browse/PROJ-456"' : ',"EXT-456","https://tracker.example.com/issues/456"')
      : '';
    const trackerVals3 = trackerEnabled ? ',"",""' : '';
    const templateData = `Summary,Folder,Category,Description,Steps,Expected Result,Suite,Linked Requirement Keys${trackerCols},Priority,Status,Severity
"Login with Valid Credentials","Functional","Authentication","Verify user can login with valid credentials","1. Navigate to login page; 2. Enter valid email; 3. Enter valid password; 4. Click Login","User is redirected to dashboard","Smoke Tests",""${trackerVals1},"High","Ready","Medium"
"Form Validation on Registration","Functional","Validation","Test form validation on user registration","1. Navigate to registration form; 2. Leave required fields empty; 3. Click submit","Error messages appear for empty required fields","Regression","REQ-001;REQ-002"${trackerVals2},"Medium","Draft","Low"
"Homepage Load Time","Performance","Performance","Verify homepage loads within acceptable time","1. Open homepage; 2. Record load time","Page loads within 2 seconds","","REQ-005"${trackerVals3},"Medium","Ready","Medium"`;

    // Create blob and download
    const blob = new Blob([templateData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'test-cases-template.csv');
    document.body.appendChild(link);
    link.click();
    link.parentElement.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    try {
      setImportLoading(true);
      setError('');
      const fileContent = await importFile.text();
      
      // Build request body - projectId is optional, backend will extract from CSV
      const requestBody = {
        csvData: fileContent,
      };
      
      // Include projectId if available (from URL context)
      if (projectId) {
        requestBody.projectId = projectId;
      }
      
      const response = await api.post('/testcases/import/csv', requestBody);

      // Store import results to display as alerts
      setImportResult({
        type: 'success',
        count: response.data.imported,
        warnings: response.data.warnings || [],
        errors: response.data.errors || []
      });
      
      setImportFile(null);
      setShowImportModal(false);
      fetchCases();
    } catch (err) {
      const errorData = err.response?.data;
      setImportResult({
        type: 'error',
        message: 'Failed to import test cases: ' + (errorData?.error || errorData?.message || err.message),
        errors: errorData?.errors || [],
        warnings: errorData?.warnings || []
      });
      setShowImportModal(false);
    } finally {
      setImportLoading(false);
    }
  };

  // ── JIRA Handlers ──────────────────────────────────────────────────────────

  const openJiraLink = tc => {
    setJiraIssueKey('');
    setJiraError('');
    setJiraLinkMode('key');
    setJiraSearchResults([]);
    setJiraSearchError('');
    setJiraModal({ mode: 'link', testCase: tc });
  };

  const openJiraCreate = tc => {
    setJiraProjectKey('');
    setJiraIssueType('Bug');
    setJiraError('');
    setJiraModal({ mode: 'create', testCase: tc });
  };

  const closeJiraModal = () => {
    setJiraModal(null);
    setJiraIssueKey('');
    setJiraProjectKey('');
    setJiraError('');
    setJiraSearchResults([]);
    setJiraSearching(false);
    setJiraSearchError('');
    setJiraLinkMode('key');
    setSearchProjectKey('');
    setSearchIssueType('');
  };

  const handleJiraLink = async () => {
    if (!jiraIssueKey.trim()) { setJiraError('Enter a JIRA issue key'); return; }
    setJiraLoading(true);
    setJiraError('');
    try {
      await api.post(`/jira/testcases/${jiraModal.testCase._id || jiraModal.testCase.id}/link`, {
        issueKey: jiraIssueKey.trim().toUpperCase(),
      });
      closeJiraModal();
      fetchCases();
    } catch (err) {
      setJiraError(err.response?.data?.error || err.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraCreate = async () => {
    if (!jiraProjectKey.trim()) { setJiraError('Enter a JIRA project key'); return; }
    setJiraLoading(true);
    setJiraError('');
    try {
      await api.post(`/jira/testcases/${jiraModal.testCase._id || jiraModal.testCase.id}/create-issue`, {
        projectKey: jiraProjectKey.trim().toUpperCase(),
        issueType: jiraIssueType,
      });
      closeJiraModal();
      fetchCases();
    } catch (err) {
      setJiraError(err.response?.data?.error || err.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraUnlink = async tcId => {
    if (!window.confirm('Unlink this JIRA issue?')) return;
    try {
      await api.delete(`/jira/testcases/${tcId}/link`);
      fetchCases();
    } catch (err) {
      setError('Failed to unlink JIRA issue: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleJiraSync = async tc => {
    try {
      await api.post(`/jira/testcases/${tc._id || tc.id}/sync`);
      fetchCases();
    } catch (err) {
      setError('JIRA sync failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const loadJiraProjects = async () => {
    try {
      const res = await api.get('/jira/projects');
      setJiraProjects(res.data.data || []);
      setJiraConnected(true);
    } catch (err) {
      setJiraConnected(false);
      setJiraError('JIRA not connected. Configure JIRA in Settings.');
    }
  };

  const handleJiraSearch = async () => {
    if (!searchProjectKey) { setJiraSearchError('Select a JIRA project'); return; }
    setJiraSearching(true);
    setJiraSearchError('');
    setJiraSearchResults([]);
    try {
      let jql = `project = "${searchProjectKey}"`;
      if (searchIssueType) jql += ` AND issuetype = "${searchIssueType}"`;
      jql += ' ORDER BY updated DESC';
      const res = await api.get('/jira/issues', { params: { jql, maxResults: 20 } });
      setJiraSearchResults(res.data.data || []);
      if ((res.data.data || []).length === 0) setJiraSearchError('No issues found');
    } catch (err) {
      setJiraSearchError(err.response?.data?.error || 'Search failed');
    } finally {
      setJiraSearching(false);
    }
  };

  const handleJiraSearchSelect = async (issue) => {
    try {
      await api.post(`/jira/testcases/${jiraModal.testCase._id || jiraModal.testCase.id}/link`, {
        issueKey: issue.key
      });
      closeJiraModal();
      fetchCases();
    } catch (err) {
      setJiraSearchError(err.response?.data?.error || 'Link failed');
    }
  };

  const getJiraStatusBadge = status => {
    if (!status) return <span className="badge bg-secondary">Unknown</span>;
    const s = status.toLowerCase();
    if (s.includes('done') || s.includes('closed') || s.includes('resolved'))
      return <span className="badge bg-success">{status}</span>;
    if (s.includes('progress') || s.includes('review'))
      return <span className="badge bg-primary">{status}</span>;
    if (s.includes('blocked')) return <span className="badge bg-danger">{status}</span>;
    return <span className="badge bg-secondary">{status}</span>;
  };

  // Filter cases based on search and suite filter
  const filteredCases = cases.filter(testCase => {
    const matchesSearch =
      !searchTerm ||
      testCase.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testCase.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSuite =
      !filterSuite ||
      (typeof testCase.testSuite === 'object'
        ? testCase.testSuite?._id === filterSuite || testCase.testSuite?.id === filterSuite
        : testCase.testSuite === filterSuite);

    return matchesSearch && matchesSuite;
  });

  return (
    <>
      {showModal ? (
        <TestCaseForm
          testCase={editingCase}
          suites={suites}
          projectId={projectId}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          loading={loading}
          error={error}
        />
      ) : (
        <div className="container mt-4">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb mb-0" style={{ background: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item">
                <Link to="/projects">Projects</Link>
              </li>
              {projectKey && project && (
                <li className="breadcrumb-item">
                  <Link to={`/projects/${projectKey.toLowerCase()}`}>{project?.name}</Link>
                </li>
              )}
              {currentFolder ? (
                <>
                  <li className="breadcrumb-item">
                    <Link to={`/projects/${projectKey.toLowerCase()}/testcases`}>Test Cases</Link>
                  </li>
                  <li className="breadcrumb-item active">📁 {currentFolder.name}</li>
                </>
              ) : currentSuite ? (
                <>
                  <li className="breadcrumb-item">
                    <Link to={`/projects/${projectKey.toLowerCase()}/testcases`}>Test Cases</Link>
                  </li>
                  <li className="breadcrumb-item active">📦 {currentSuite.name}</li>
                </>
              ) : folderId === 'none' ? (
                <>
                  <li className="breadcrumb-item">
                    <Link to={`/projects/${projectKey.toLowerCase()}/testcases`}>Test Cases</Link>
                  </li>
                  <li className="breadcrumb-item active">Unassigned</li>
                </>
              ) : (
                <li className="breadcrumb-item active">Test Cases</li>
              )}
            </ol>
          </nav>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 style={{ marginTop: '0.5rem' }}>
            {currentFolder ? `📁 ${currentFolder.name}` : currentSuite ? `📦 ${currentSuite.name}` : folderId === 'none' ? '📂 Unassigned' : 'Test Cases'}
          </h2>
          {selectedCases.length > 0 && (
            <small className="text-muted">
              {selectedCases.length} selected
            </small>
          )}
        </div>
        <div className="btn-group" role="group">
          {selectedCases.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={() => handleBulkDelete()}
              title="Delete selected test cases"
            >
              🗑️ Delete Selected ({selectedCases.length})
            </button>
          )}
          <Link 
            to={`/projects/${projectKey}/folders/testcase`}
            className="btn btn-secondary"
            title="Manage folders"
          >
            📁 Manage Folders
          </Link>
          <button className="btn btn-success" onClick={() => setShowExportModal(true)} title="Export test cases to CSV">
            📥 Export CSV
          </button>
          <button className="btn btn-info" onClick={() => setShowImportModal(true)} title="Import test cases from CSV">
            📤 Import CSV
          </button>
          <button className="btn btn-primary" onClick={openCreateModal}>
            ➕ Create Test Case
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Import Result Alerts */}
      {importResult && (
        <div style={{ marginBottom: '1rem' }}>
          {importResult.type === 'success' && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <strong>Success!</strong> {importResult.count} test case(s) imported.
              <button type="button" className="btn-close" onClick={() => setImportResult(null)}></button>
            </div>
          )}
          
          {importResult.warnings && importResult.warnings.length > 0 && (
            <div className="alert alert-warning alert-dismissible fade show" role="alert">
              <strong>Warnings:</strong>
              <ul className="mb-0 mt-2">
                {importResult.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
              <button type="button" className="btn-close" onClick={() => setImportResult(prev => ({ ...prev, warnings: [] }))}></button>
            </div>
          )}
          
          {importResult.type === 'error' && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <strong>Error:</strong> {importResult.message}
              {importResult.errors && importResult.errors.length > 0 && (
                <ul className="mb-0 mt-2">
                  {importResult.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              )}
              <button type="button" className="btn-close" onClick={() => setImportResult(null)}></button>
            </div>
          )}
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by summary or description..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <select
                className="form-select"
                value={filterSuite}
                onChange={e => setFilterSuite(e.target.value)}
              >
                <option value="">All Suites</option>
                {suites.map(suite => (
                  <option key={suite._id || suite.id} value={suite._id || suite.id}>
                    {suite.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <TestCaseForm
          testCase={editingCase}
          suites={suites}
          projectId={projectId}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          loading={loading}
          error={error}
        />
      )}

      {/* Test Cases List */}
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">Test Cases ({filteredCases.length})</h5>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredCases.length === 0 ? (
            <p className="text-muted">
              {cases.length === 0
                ? 'No test cases found. Create one to get started.'
                : 'No test cases match your search criteria.'}
            </p>
          ) : (
            <>
            <ResizableTable tableRef={tableRef} className="table-hover">
                <thead>
                  <tr>
                    <ResizableHeader colKey="check" width={colWidths.check} onMouseDown={onMouseDown}>
                      <input 
                        type="checkbox" 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCases(filteredCases.map(tc => tc._id || tc.id));
                          } else {
                            setSelectedCases([]);
                          }
                        }}
                        checked={selectedCases.length === filteredCases.length && filteredCases.length > 0}
                        title="Select all"
                      />
                    </ResizableHeader>
                    <ResizableHeader colKey="id" width={colWidths.id} onMouseDown={onMouseDown}>ID</ResizableHeader>
                    <ResizableHeader colKey="summary" width={colWidths.summary} onMouseDown={onMouseDown}>Summary</ResizableHeader>
                    <ResizableHeader colKey="suite" width={colWidths.suite} onMouseDown={onMouseDown}>Test Suite</ResizableHeader>
                    <ResizableHeader colKey="steps" width={colWidths.steps} onMouseDown={onMouseDown}>Steps</ResizableHeader>
                    {trackerEnabled && <ResizableHeader colKey="tracker" width={colWidths.tracker} onMouseDown={onMouseDown}>{trackerLabels.columnHeader}</ResizableHeader>}
                    <ResizableHeader colKey="actions" width={colWidths.actions} onMouseDown={onMouseDown}>Actions</ResizableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredCases.map(testCase => (
                    <tr key={testCase._id || testCase.id}>
                      <td>
                        <input 
                          type="checkbox"
                          checked={selectedCases.includes(testCase._id || testCase.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCases([...selectedCases, testCase._id || testCase.id]);
                            } else {
                              setSelectedCases(selectedCases.filter(id => id !== (testCase._id || testCase.id)));
                            }
                          }}
                        />
                      </td>
                      <td>
                        <Link 
                          to={`/projects/${projectKey}/testcases/${testCase._id || testCase.id}`}
                          className="text-decoration-none"
                          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                        >
                          {testCase.testCaseId || (testCase._id || testCase.id).toString().slice(-8)}
                        </Link>
                      </td>
                      <td className="cell-wrap">
                        <strong>{testCase.title}</strong>
                      </td>
                      <td>
                        {typeof testCase.testSuite === 'object'
                          ? testCase.testSuite?.name
                          : testCase.testSuite || 'N/A'}
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {Array.isArray(testCase.steps)
                            ? testCase.steps.length
                            : testCase.steps?.split('\n').filter(s => s.trim()).length || 0}{' '}
                          steps
                        </span>
                      </td>
                      {trackerEnabled && (
                      <td>
                        {testCase.jira?.issueKey ? (
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <a
                              href={testCase.jira.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="badge bg-primary text-decoration-none"
                            >
                              {testCase.jira.issueKey}
                            </a>
                            {getJiraStatusBadge(testCase.jira.status)}
                            {isJira && (
                              <button
                                className="btn btn-sm btn-outline-secondary py-0 px-1"
                                title={trackerLabels.syncAction}
                                onClick={() => handleJiraSync(testCase)}
                              >
                                🔄
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-danger py-0 px-1"
                              title="Unlink"
                              onClick={() => handleJiraUnlink(testCase._id || testCase.id)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary py-0 px-1"
                              title={trackerLabels.linkAction}
                              onClick={() => openJiraLink(testCase)}
                            >
                              🔗 Link
                            </button>
                            {isJira && (
                              <button
                                className="btn btn-sm btn-outline-secondary py-0 px-1"
                                title={trackerLabels.createAction}
                                onClick={() => openJiraCreate(testCase)}
                              >
                                ➕
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      )}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* Actions dropdown */}
                        <div
                          className="dropdown"
                          style={{ position: 'relative', display: 'inline-block' }}
                          onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpenActionMenu(null); }}
                          ref={el => {
                            if (el && openActionMenu === (testCase._id || testCase.id)) {
                              const menu = el.querySelector('.dropdown-menu');
                              if (menu) {
                                const rect = menu.getBoundingClientRect();
                                const spaceBelow = window.innerHeight - rect.bottom;
                                if (spaceBelow < 0) {
                                  menu.style.bottom = '100%';
                                  menu.style.top = 'auto';
                                } else {
                                  menu.style.top = '100%';
                                  menu.style.bottom = 'auto';
                                }
                              }
                            }
                          }}
                        >
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            style={{ padding: '3px 10px', fontSize: 13 }}
                            onClick={() => setOpenActionMenu(openActionMenu === (testCase._id || testCase.id) ? null : (testCase._id || testCase.id))}
                            title="Actions"
                          >
                            ⋯
                          </button>
                          {openActionMenu === (testCase._id || testCase.id) && (
                            <ul
                              className="dropdown-menu show"
                              style={{
                                position: 'absolute', right: 0, left: 'auto', top: '100%',
                                zIndex: 1050, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
                                marginTop: '4px', maxHeight: '300px', overflowY: 'auto',
                              }}
                            >
                              <li>
                                <button className="dropdown-item" onClick={() => { openEditModal(testCase); setOpenActionMenu(null); }}>
                                  ✏️ Edit
                                </button>
                              </li>
                              <li>
                                <Link
                                  to={`/projects/${projectKey}/testcases/${testCase._id || testCase.id}`}
                                  className="dropdown-item"
                                  onClick={() => setOpenActionMenu(null)}
                                >
                                  👁️ View Details
                                </Link>
                              </li>
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <button className="dropdown-item text-danger" onClick={() => { handleDelete(testCase._id || testCase.id); setOpenActionMenu(null); }}>
                                  🗑️ Delete
                                </button>
                              </li>
                            </ul>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>

            {/* Pagination */}
            {cases.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                limit={pageSize}
                total={totalItems}
                onPageChange={setCurrentPage}
                onLimitChange={(newLimit) => {
                  setPageSize(newLimit);
                  setCurrentPage(1);
                }}
                isLoading={loading}
              />
            )}
            </>
          )}
        </div>
      </div>

      {/* JIRA Link / Create Modal */}
      {jiraModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {jiraModal.mode === 'link' ? `🔗 ${trackerLabels.linkAction}` : `➕ ${trackerLabels.createAction}`}
                </h5>
                <button type="button" className="btn-close" onClick={closeJiraModal}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  <strong>{jiraModal.testCase.title}</strong>
                </p>
                {jiraError && <div className="alert alert-danger py-2">{jiraError}</div>}
                
                {jiraModal.mode === 'link' ? (
                  <div>
                    {/* Mode selection tabs */}
                    <div className="nav nav-tabs mb-3" role="tablist">
                      <button
                        className={`nav-link ${jiraLinkMode === 'key' ? 'active' : ''}`}
                        onClick={() => { setJiraLinkMode('key'); setJiraSearchError(''); }}
                      >
                        📝 By Issue Key
                      </button>
                      {isJira && (
                      <button
                        className={`nav-link ${jiraLinkMode === 'search' ? 'active' : ''}`}
                        onClick={() => {
                          setJiraLinkMode('search');
                          setJiraSearchError('');
                          if (!jiraProjects.length) loadJiraProjects();
                        }}
                      >
                        🔍 Search JIRA
                      </button>
                      )}
                    </div>

                    {jiraLinkMode === 'key' ? (
                      /* Issue key input */
                      <div className="mb-3">
                        <label className="form-label">{trackerLabels.idLabel} *</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={isJira ? 'e.g. PROJ-123' : 'e.g. #123'}
                          value={jiraIssueKey}
                          onChange={e => setJiraIssueKey(isJira ? e.target.value.toUpperCase() : e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleJiraLink()}
                        />
                        <small className="text-muted">Enter the {trackerLabels.idLabel} to link.</small>
                      </div>
                    ) : (
                      /* Search mode */
                      <div>
                        {!jiraConnected ? (
                          <div className="alert alert-warning">JIRA not configured. Go to Settings → JIRA Integration.</div>
                        ) : (
                          <div>
                            <div className="mb-3">
                              <label className="form-label">JIRA Project *</label>
                              <select
                                className="form-select"
                                value={searchProjectKey}
                                onChange={e => {
                                  setSearchProjectKey(e.target.value);
                                  const proj = jiraProjects.find(p => p.key === e.target.value);
                                  setIssueTypes(proj?.issueTypes || []);
                                  setSearchIssueType('');
                                }}
                              >
                                <option value="">Select a project...</option>
                                {jiraProjects.map(p => (
                                  <option key={p.key} value={p.key}>{p.name} ({p.key})</option>
                                ))}
                              </select>
                            </div>
                            <div className="mb-3">
                              <label className="form-label">Issue Type (Optional)</label>
                              <select
                                className="form-select"
                                value={searchIssueType}
                                onChange={e => setSearchIssueType(e.target.value)}
                                disabled={!searchProjectKey}
                              >
                                <option value="">All types</option>
                                {issueTypes.map(t => (
                                  <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                              </select>
                            </div>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleJiraSearch}
                              disabled={!searchProjectKey || jiraSearching}
                            >
                              {jiraSearching ? <>🔍 Searching...</> : '🔍 Search Issues'}
                            </button>
                          </div>
                        )}

                        {jiraSearchError && <div className="alert alert-danger mt-3 py-2">{jiraSearchError}</div>}
                        
                        {jiraSearchResults.length > 0 && (
                          <div className="mt-3">
                            <label className="form-label">Found {jiraSearchResults.length} issue(s):</label>
                            <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                              {jiraSearchResults.map(issue => (
                                <div
                                  key={issue.key}
                                  style={{
                                    padding: '10px',
                                    borderBottom: '1px solid #eee',
                                    cursor: 'pointer',
                                    background: 'white',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                  onClick={() => handleJiraSearchSelect(issue)}
                                >
                                  <div style={{ fontWeight: 600, color: '#0066cc' }}>{issue.key}</div>
                                  <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{issue.summary}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Create mode */
                  <>
                    <div className="mb-3">
                      <label className="form-label">JIRA Project Key *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. PROJ"
                        value={jiraProjectKey}
                        onChange={e => setJiraProjectKey(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Issue Type</label>
                      <select
                        className="form-select"
                        value={jiraIssueType}
                        onChange={e => setJiraIssueType(e.target.value)}
                      >
                        <option>Bug</option>
                        <option>Task</option>
                        <option>Story</option>
                        <option>Improvement</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeJiraModal} disabled={jiraLoading || jiraSearching}>
                  Cancel
                </button>
                {jiraModal.mode === 'link' && jiraLinkMode === 'key' && (
                  <button
                    className="btn btn-primary"
                    onClick={handleJiraLink}
                    disabled={jiraLoading}
                  >
                    {jiraLoading ? <>🔗 Linking...</> : '🔗 Link Issue'}
                  </button>
                )}
                {jiraModal.mode === 'create' && (
                  <button
                    className="btn btn-primary"
                    onClick={handleJiraCreate}
                    disabled={jiraLoading}
                  >
                    {jiraLoading ? <>➕ Creating...</> : '➕ Create Issue'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Export Test Cases to CSV</h5>
                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Export all test cases to CSV format for backup or sharing.</p>
                <div className="alert alert-info">
                  <small>
                    <strong>Note:</strong> This will export {cases.length} test case(s) in CSV format.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                  disabled={exportLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleExportCSV}
                  disabled={exportLoading || cases.length === 0}
                >
                  {exportLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Test Cases from CSV</h5>
                <button type="button" className="btn-close" onClick={() => setShowImportModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p className="text-muted small mb-2">
                    Need help with the format? Download our template CSV file:
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleDownloadTemplate}
                  >
                    📋 Download Template
                  </button>
                </div>
                <hr className="my-3" />
                <div className="mb-3">
                  <label htmlFor="csvFile" className="form-label">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="csvFile"
                    accept=".csv"
                    onChange={e => setImportFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="alert alert-info">
                  <small>
                    <strong>CSV Format:</strong> Required columns: Title, Steps, Expected Result, Project Key. Optional: Description, Suite, Folder, Priority, Status, Category, Severity, Owner, JIRA Issue Key, JIRA URL, JIRA Status, Linked Requirement Keys (comma/semicolon-separated).
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportModal(false)}
                  disabled={importLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleImportCSV}
                  disabled={importLoading || !importFile}
                >
                  {importLoading ? 'Importing...' : 'Import CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      )}
    </>
  );
}
